/**
 * AuthContext — single source of truth for "who is logged in" across
 * the React tree. Hydrates on first mount by asking amazon-cognito-
 * identity-js if there's a valid session in browser storage; that
 * avoids bouncing the user to /login on every page reload.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  AuthUser,
  SignInResult,
  confirmPasswordReset,
  getCurrentUser,
  requestPasswordReset,
  signIn as doSignIn,
  signOut as doSignOut,
} from './auth';

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  /**
   * Attempt sign-in. Returns the SignInResult so the caller can
   * distinguish a direct success from a newPasswordRequired challenge.
   * On a direct success the context's user is updated automatically.
   */
  signIn: (username: string, password: string) => Promise<SignInResult>;
  /** Manually refresh the current user from the browser's Cognito session. */
  refresh: () => Promise<void>;
  signOut: () => void;
  requestPasswordReset: (email: string) => Promise<void>;
  confirmPasswordReset: (email: string, code: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getCurrentUser().then((u) => {
      if (!cancelled) {
        setUser(u);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(
    async (username: string, password: string): Promise<SignInResult> => {
      const result = await doSignIn(username, password);
      if (result.kind === 'success') {
        setUser(result.user);
      }
      return result;
    },
    [],
  );

  const refresh = useCallback(async () => {
    const u = await getCurrentUser();
    setUser(u);
  }, []);

  const signOut = useCallback(() => {
    doSignOut();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      signIn,
      refresh,
      signOut,
      requestPasswordReset,
      confirmPasswordReset,
    }),
    [user, loading, signIn, refresh, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
